/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { data, redirect, redirectDocument, useActionData } from 'react-router';
import { usePasskeyLogin } from '@/hooks/use-passkey-login';
import type { Route } from './+types/_empty.login';
import { Link } from '@/components/link';
import { Card } from '@/components/ui/card';
import { routes } from '@/route-paths';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import OtpModal from '@/components/login/otp-modal';
import { isAbsoluteURL, getSafeReturnUrl } from '@/lib/utils';
import { getAppOrigin } from '@/lib/origin';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { getLoginPreferences } from '@/lib/login-preferences.server';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { updateBasketResource } from '@/middlewares/basket.server';
import { buildUrlFromContext } from '@/lib/url.server';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { getTurnstileSiteKey, getTurnstileMode, isTurnstileEnabled } from '@/lib/turnstile/utils';

// services
import {
    getAuth,
    authorizePasswordless,
    getPasswordLessAccessToken,
    updateAuth as updateAuthServer,
} from '@/middlewares/auth.server';
import { loginRegisteredUser } from '@/lib/api/auth/standard-login.server';
import { authorizeIDP } from '@/lib/api/auth/social-login.server';
import { mergeBasket } from '@/lib/api/basket.server';
import { getCustomer } from '@/lib/api/customer.server';
import { createApiClients } from '@/lib/api-clients.server';
import {
    appendWishlistMergeFlag,
    captureGuestWishlistSnapshot,
    mergeWishlist,
    type WishlistMergeResult,
} from '@/lib/api/wishlist.server';
import { getPasswordlessErrorMessageKey, extractErrorMessage } from '@/lib/auth/error-handler';
import { getLogger } from '@/lib/logger.server';
import { enforceTurnstile } from '@/lib/turnstile/enforce.server';
import { COOKIE_TURNSTILE_VERIFIED, TURNSTILE_VERIFIED_MAX_AGE } from '@/lib/turnstile/constants';
import { createCookie, getCookieConfig, getCookieNameWithSiteId } from '@/lib/cookie-utils.server';
import { ApiError } from '@/scapi';
// @priceshoes-block-start LOGIN_IDENTIFICATION
import IdentificationForm from '@/extensions/priceshoes/components/login/identification-form';
import PasswordForm from '@/extensions/priceshoes/components/login/password-form';
import { validateIdentifier } from '@/extensions/priceshoes/lib/auth/identifier';
import { validatePassword } from '@/extensions/priceshoes/lib/auth/password';
import { loginCustomer } from '@/extensions/priceshoes/lib/auth/login-customer.server';
import { startCustomerSession } from '@/extensions/priceshoes/lib/auth/start-session.server';
import { serializeCognitoCookies } from '@/extensions/priceshoes/lib/auth/cognito-cookies.server';
import { type CustomerStatus, identifyCustomer } from '@/extensions/priceshoes/lib/auth/identify-customer.server';
// @priceshoes-block-end LOGIN_IDENTIFICATION
// @priceshoes-block-start LOGIN_VALIDATION_METHOD
import ValidationMethodForm from '@/extensions/priceshoes/components/login/validation-method-form';
import {
    type ValidationMethod,
    type ValidationMethodType,
    isValidationMethodType,
    parseValidationMethods,
} from '@/extensions/priceshoes/lib/auth/validation-method';
// @priceshoes-block-end LOGIN_VALIDATION_METHOD
// @priceshoes-block-start LOGIN_IDENTITY_VERIFICATION
import VerificationCodeForm from '@/extensions/priceshoes/components/login/verification-code-form';
import IdentityVerified from '@/extensions/priceshoes/components/login/identity-verified';
import { readDisabledMethods, readIdentityStep } from '@/extensions/priceshoes/lib/auth/identity-step';
import { validateVerificationCode } from '@/extensions/priceshoes/lib/auth/verification-code';
import SecurityQuestionsForm from '@/extensions/priceshoes/components/login/security-questions-form';
import {
    type SecurityQuestion,
    parseSecurityQuestions,
    readSecurityAnswers,
} from '@/extensions/priceshoes/lib/auth/security-questions';
import {
    type CodeValidationMethod,
    getSecurityQuestions,
    sendVerificationCode,
    verifyIdentity,
} from '@/extensions/priceshoes/lib/auth/identity-verification.server';
import {
    IDENTITY_FLOW_FIELD,
    openIdentityFlow,
    sealIdentityFlow,
} from '@/extensions/priceshoes/lib/auth/identity-flow.server';
// @priceshoes-block-end LOGIN_IDENTITY_VERIFICATION

type LoginActionResponse = {
    success: boolean;
    error?: string;
    /**
     * Machine-readable error code for client-side handling. `NOT_AUTHORIZED` indicates
     * a Turnstile gate rejection so the form can reset the widget and show a generic
     * verification message without leaking detection signals. See README-TURNSTILE.md.
     */
    errorCode?: string;
    showOTPForm?: boolean;
    email?: string;
    /** @priceshoes-line LOGIN_IDENTIFICATION - Auth Extension status of the identified shopper. */
    customerStatus?: CustomerStatus;
    // @priceshoes-block-start LOGIN_PASSWORD
    /** Step to render after the action; `password` shows the password screen (PRIC-171). */
    step?: 'password';
    /** Identifier the shopper entered, kept for the password step (it is the shopper's own input). */
    identifier?: string;
    // @priceshoes-block-end LOGIN_PASSWORD
    // @priceshoes-block-start LOGIN_VALIDATION_METHOD
    /** Identity validation methods of a PENDING shopper (PRIC-167); shows the method-selection screen. */
    validations?: ValidationMethod[];
    /** Method the shopper chose on the method-selection screen. */
    selectedMethod?: ValidationMethodType;
    // @priceshoes-block-end LOGIN_VALIDATION_METHOD
    // @priceshoes-block-start LOGIN_IDENTITY_VERIFICATION
    /** Medium the verification code was sent to; shows the verification code screen (PRIC-168). */
    verificationMethod?: CodeValidationMethod;
    /** Security questions to answer; shows the security questions screen (PRIC-168). */
    securityQuestions?: SecurityQuestion[];
    /** Soci@ ID of a BLOCKED shopper; shows the "update your contact details" variant of the method selection. */
    partnerId?: string;
    /** Methods disabled on the method selection (security questions whose attempts ran out). */
    disabledMethods?: ValidationMethodType[];
    /** Confirmation after resending the code. */
    codeResent?: boolean;
    /** The shopper is locked after too many failed attempts. */
    verificationLocked?: boolean;
    /** Identity validated: continues to the create-password step (PRIC-169). */
    identityVerified?: boolean;
    /** Identity validation in progress, encrypted (customer ID and, once validated, the change-password token). */
    identityFlow?: string;
    // @priceshoes-block-end LOGIN_IDENTITY_VERIFICATION
};

type LoginLoaderData = {
    error?: string;
    passwordlessSent?: boolean;
    email?: string;
    mode: string;
    isPasswordlessLoginEnabled: boolean;
    isSocialLoginEnabled: boolean;
    returnUrl?: string | null;
    action?: string | null;
    actionParams?: string | null;
    showOTPForm?: boolean;
    otpLength?: number;
    pageUrl: string;
    /** Number of items in the guest wishlist (0 if guest has none, no list, or SCAPI failed). */
    guestWishlistCount: number;
};

export async function loader({ request, context }: Route.LoaderArgs) {
    const logger = getLogger(context);
    const session = getAuth(context);
    const url = new URL(request.url);
    const pageUrl = buildCanonicalUrl(url.origin, url.pathname, url.search);
    const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));

    // If user is already logged in as registered user, redirect to returnUrl or home
    const { accessToken, accessTokenExpiry, userType, customerId } = session;
    if (
        accessToken &&
        typeof accessTokenExpiry === 'number' &&
        accessTokenExpiry >= Date.now() &&
        userType === 'registered' &&
        customerId
    ) {
        logger.debug('Login: already authenticated, redirecting');
        return redirect(buildUrlFromContext(returnUrl || routes.home, context));
    }

    const passwordlessSent = url.searchParams.get('passwordless') === 'sent';
    const email = url.searchParams.get('email');

    const tokenFromUrl = url.searchParams.get('token');
    const legacyOtpCode = url.searchParams.get('otpCode');
    const token = tokenFromUrl ?? legacyOtpCode;
    const pendingAction = url.searchParams.get('action');
    const actionParams = url.searchParams.get('actionParams');
    const error = url.searchParams.get('error');

    const config = getConfig(context);
    // To enable passwordless login, the "Enable Email Verification" site preference under "Storefront Login Preferences" must be enabled.
    const { emailVerificationEnabled } = await getLoginPreferences(context);
    const isPasswordlessLoginEnabled = Boolean(emailVerificationEnabled);
    const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);

    // Ignore `?mode` when passwordless is disabled - force standard password login so the
    // passwordless form (and its Turnstile widget) cannot render for a flow that's turned off.
    const mode = isPasswordlessLoginEnabled ? url.searchParams.get('mode') || 'passwordless' : 'password';
    // Ignore `?otp=true` when passwordless is disabled - suppresses the OTP modal and its
    // Turnstile widget when the email-verification site permission is off.
    const showOTPForm = isPasswordlessLoginEnabled && url.searchParams.get('otp') === 'true';

    // Auto-verify OTP token if both email and token are provided in URL
    if (email && token) {
        // Snapshot the guest wishlist BEFORE the SLAS swap; the registered token can't authorize a read against the guest customerId.
        const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

        try {
            const tokenResponse = await getPasswordLessAccessToken(context, token);

            // Update session with auth data. userType, customerId, usid, and the refresh-token
            // expiry cap all derive from the access-token JWT inside updateAuth; no follow-up
            // call is needed.
            updateAuthServer(context, tokenResponse);

            // Merge guest basket with registered user basket
            try {
                await mergeBasket(context);
            } catch (basketError) {
                logger.error('Login: basket merge failed during passwordless login', { error: basketError });
            }

            let wishlistMergeResult: WishlistMergeResult | null = null;
            if (guestWishlistSnapshot) {
                try {
                    wishlistMergeResult = await mergeWishlist(context, guestWishlistSnapshot);
                } catch (wishlistError) {
                    logger.error('Login: wishlist merge failed during passwordless login', { error: wishlistError });
                }
            }

            logger.info('Login: passwordless verification succeeded');
            const target = buildUrlFromContext(returnUrl || routes.home, context);
            if (wishlistMergeResult) {
                const { url: redirectUrl, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                return redirect(redirectUrl, { headers: { 'Set-Cookie': setCookie } });
            }
            return redirect(target);
        } catch (verifyError) {
            // Auto-verification failed - show error with OTP form
            logger.warn('Login: passwordless auto-verification failed');
            const errorMessage = extractErrorMessage(verifyError);
            const errorKey = getPasswordlessErrorMessageKey(errorMessage);
            const { t } = getTranslation(context);

            return {
                error: t(errorKey),
                showOTPForm: isPasswordlessLoginEnabled,
                email,
                mode,
                isPasswordlessLoginEnabled,
                isSocialLoginEnabled,
                returnUrl,
                action: pendingAction,
                actionParams,
                otpLength: config.auth.otpLength,
                pageUrl,
                guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
            };
        }
    }

    // Drives the guest-saved-items banner above the form. Helper gates on guest userType
    // and swallows SCAPI errors; a missing snapshot must not block sign-in.
    const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

    const { t } = getTranslation(context);
    const resolvedError = error === 'session_expired' ? t('errors:api.unauthorized') : error;

    return {
        error: resolvedError,
        passwordlessSent,
        showOTPForm,
        email,
        mode,
        isPasswordlessLoginEnabled,
        isSocialLoginEnabled,
        returnUrl,
        action: pendingAction,
        actionParams,
        otpLength: config.auth.otpLength,
        pageUrl,
        guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
    };
}

// @priceshoes-block-start LOGIN_IDENTITY_VERIFICATION
/**
 * Identity validated (PRIC-168 CA5, CA10): continues to the create-password step (PRIC-169) carrying the
 * change-password token, encrypted. Null when the flow can't be encrypted (PRICESHOES_COOKIE_SECRET missing).
 */
function identityVerifiedStep(
    context: Route.ActionArgs['context'],
    identifier: string,
    flow: { customerId: string; changePasswordToken: string }
): LoginActionResponse | null {
    const identityFlow = sealIdentityFlow(context, flow);
    return identityFlow
        ? { success: true, customerStatus: 'PENDING', identifier, identityVerified: true, identityFlow }
        : null;
}

/**
 * The security questions attempts ran out (PRIC-168 CA8): Auth Extension marks the shopper BLOCKED, so they're
 * identified again to show the "update your contact details" screen with the methods left (email, phone).
 * Falls back to the method selection with the questions disabled if Auth Extension doesn't report BLOCKED.
 */
async function questionsLockedStep(
    context: Route.ActionArgs['context'],
    identifier: string,
    fallback: LoginActionResponse
): Promise<LoginActionResponse> {
    const identification = await identifyCustomer(context, identifier);
    return identification.result === 'found' && identification.status === 'BLOCKED'
        ? {
              success: true,
              customerStatus: identification.status,
              identifier,
              validations: identification.validations,
              partnerId: identification.partnerId,
          }
        : fallback;
}
// @priceshoes-block-end LOGIN_IDENTITY_VERIFICATION

/**
 * Server action for authentication. Login is handled server-side for security (httpOnly cookies,
 * PKCE code verifier storage) and integration with SLAS.
 *
 * Returns `LoginActionResponse | Response | ReturnType<data<LoginActionResponse>>` because the action has three distinct outcomes:
 * - `Response` (redirect): successful login or social IDP authorization. React Router intercepts
 *   the redirect before it reaches the component, so `useActionData()` never sees it.
 * - `data(LoginActionResponse, { headers })`: passwordless OTP intermediate state that also sets
 *   the Turnstile `cc-tv` cookie via Set-Cookie.
 * - `LoginActionResponse`: errors or intermediate states without extra headers. This data is
 *   serialized and delivered to the component via `useActionData()` for rendering.
 */
export async function action({
    request,
    context,
}: Route.ActionArgs): Promise<LoginActionResponse | Response | ReturnType<typeof data<LoginActionResponse>>> {
    const logger = getLogger(context);
    const config = getConfig(context);
    const { t } = getTranslation(context);
    const genericError = t('errors:genericTryAgain');

    try {
        const formData = await request.formData();

        const email = formData.get('email')?.toString();
        const password = formData.get('password')?.toString();
        const loginMode = formData.get('loginMode')?.toString();
        const provider = formData.get('provider')?.toString();
        const redirectPath = formData.get('redirectPath')?.toString();
        const skipUsid = formData.get('skipUsid') === 'true';
        const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);

        // @priceshoes-block-start LOGIN_IDENTIFICATION
        // Identifies the shopper by partner ID or email in Auth Extension (PRIC-165).
        if (loginMode === 'identify') {
            const identifier = formData.get('identifier')?.toString().trim() ?? '';
            const identifierError = validateIdentifier(identifier);
            if (identifierError) {
                return { success: false, error: t(`extPriceshoes:login.errors.${identifierError}`) };
            }

            const identification = await identifyCustomer(context, identifier);
            if (identification.result === 'notFound') {
                return { success: false, error: t('extPriceshoes:login.errors.notFound') };
            }
            if (identification.result === 'error') {
                return { success: false, error: t('extPriceshoes:login.errors.generic') };
            }

            // @priceshoes-block-start LOGIN_PASSWORD
            // VALID continues to the password screen (PRIC-171).
            // TODO PRIC-168: temporary for QA without PENDING users; PRICESHOES_FORCE_IDENTITY_VALIDATION=true sends
            // VALID shoppers to the identity validation too. Remove before merging.
            const forceIdentityValidation =
                process.env.PRICESHOES_FORCE_IDENTITY_VALIDATION === 'true' && identification.validations.length > 0;
            if (identification.status === 'VALID' && !forceIdentityValidation) {
                return { success: true, customerStatus: identification.status, step: 'password', identifier };
            }
            // @priceshoes-block-end LOGIN_PASSWORD

            // @priceshoes-block-start LOGIN_VALIDATION_METHOD
            // PENDING continues to the identity validation method selection (PRIC-167). BLOCKED too, on the
            // "update your contact details" variant with its Soci@ ID (PRIC-168 CA8).
            return {
                success: true,
                customerStatus: identification.status,
                identifier,
                validations: identification.validations,
                ...('partnerId' in identification ? { partnerId: identification.partnerId } : {}),
            };
            // @priceshoes-block-end LOGIN_VALIDATION_METHOD
        }
        // @priceshoes-block-end LOGIN_IDENTIFICATION

        // @priceshoes-block-start LOGIN_VALIDATION_METHOD
        // Method chosen on the identity validation screen of a PENDING shopper (PRIC-167).
        if (loginMode === 'priceshoesValidationMethod') {
            const identifier = formData.get('identifier')?.toString().trim() ?? '';
            const validations = parseValidationMethods(formData.get('validations')?.toString());
            // Without the identifier or the methods the screen can't be shown again: restart from identification.
            if (validateIdentifier(identifier) || validations.length === 0) {
                return { success: false, error: t('extPriceshoes:login.errors.generic') };
            }

            // @priceshoes-line LOGIN_IDENTITY_VERIFICATION - Keeps the "update your contact details" variant (BLOCKED).
            const partnerId = formData.get('partnerId')?.toString() || undefined;
            const pendingStep = { customerStatus: 'PENDING' as const, identifier, validations, partnerId };
            const method = formData.get('validationMethod')?.toString();
            if (!isValidationMethodType(method) || !validations.some(({ type }) => type === method)) {
                return { success: false, error: t('extPriceshoes:login.validation.errors.empty'), ...pendingStep };
            }

            // @priceshoes-block-start LOGIN_IDENTITY_VERIFICATION
            // Methods disabled on this screen (security questions whose attempts ran out) stay disabled.
            const disabledMethods = readDisabledMethods(formData);
            const selectionStep = { ...pendingStep, disabledMethods };
            const errorFor = (key: string) => t(`extPriceshoes:login.verification.errors.${key}`);
            if (disabledMethods.includes(method)) {
                return { success: false, error: errorFor('questionsLocked'), ...selectionStep };
            }

            // Email or SMS: Commerce Cloud sends the code and the verification code screen is shown (PRIC-168).
            if (method === 'email' || method === 'phone') {
                const sending = await sendVerificationCode(context, identifier, method);
                const identityFlow =
                    sending.result === 'sent' ? sealIdentityFlow(context, { customerId: sending.customerId }) : null;
                if (identityFlow) {
                    return { success: true, ...pendingStep, verificationMethod: method, identityFlow };
                }
                return {
                    success: false,
                    error: errorFor(sending.result === 'locked' ? 'locked' : 'sendFailed'),
                    ...selectionStep,
                    selectedMethod: method,
                };
            }

            // Security questions: Commerce Cloud returns the questions and the questions screen is shown (CA9).
            const questions = await getSecurityQuestions(context, identifier);
            if (questions.result === 'questions') {
                const identityFlow = sealIdentityFlow(context, { customerId: questions.customerId });
                if (identityFlow) {
                    return { success: true, ...pendingStep, securityQuestions: questions.questions, identityFlow };
                }
            }
            if (questions.result === 'locked') {
                return questionsLockedStep(context, identifier, {
                    success: false,
                    error: errorFor('questionsLocked'),
                    ...pendingStep,
                    disabledMethods: [...disabledMethods, 'questions' as const],
                });
            }
            return { success: false, error: errorFor('questionsFailed'), ...selectionStep, selectedMethod: method };
            // @priceshoes-block-end LOGIN_IDENTITY_VERIFICATION
        }
        // @priceshoes-block-end LOGIN_VALIDATION_METHOD

        // @priceshoes-block-start LOGIN_IDENTITY_VERIFICATION
        // Security questions screen: validate the answers (CA10, CA11, CA8) (PRIC-168).
        if (loginMode === 'priceshoesSecurityAnswers') {
            const step = readIdentityStep(formData);
            const securityQuestions = parseSecurityQuestions(formData.get('questions')?.toString());
            if (!step || securityQuestions.length === 0) {
                return { success: false, error: t('extPriceshoes:login.errors.generic') };
            }

            const errorFor = (key: string) => t(`extPriceshoes:login.verification.errors.${key}`);
            const flow = openIdentityFlow(formData);
            if (!flow) {
                // The validation expired (or was tampered with): choose the method again.
                return { success: false, error: errorFor('expired'), customerStatus: 'PENDING', ...step };
            }

            const questionsStep = {
                customerStatus: 'PENDING' as const,
                ...step,
                securityQuestions,
                identityFlow: formData.get(IDENTITY_FLOW_FIELD)?.toString(),
            };
            const answers = readSecurityAnswers(formData, securityQuestions);
            if (!answers) {
                return {
                    success: false,
                    error: t('extPriceshoes:login.questions.errors.unanswered'),
                    ...questionsStep,
                };
            }

            const verification = await verifyIdentity(context, flow.customerId, { answers });
            switch (verification.result) {
                case 'verified':
                    return (
                        identityVerifiedStep(context, step.identifier, {
                            customerId: flow.customerId,
                            changePasswordToken: verification.changePasswordToken,
                        }) ?? { success: false, error: errorFor('validateFailed'), ...questionsStep }
                    );
                case 'invalid':
                    return { success: false, error: errorFor('invalidAnswers'), ...questionsStep };
                case 'locked':
                    return questionsLockedStep(context, step.identifier, {
                        success: false,
                        error: errorFor('questionsLocked'),
                        customerStatus: 'PENDING',
                        ...step,
                        disabledMethods: ['questions'],
                    });
                default:
                    return { success: false, error: errorFor('validateFailed'), ...questionsStep };
            }
        }

        // Verification code screen: resend the code (CA7) or validate it (CA5, CA6, CA8) (PRIC-168).
        if (loginMode === 'priceshoesResendCode' || loginMode === 'priceshoesVerificationCode') {
            const step = readIdentityStep(formData);
            const posted = formData.get('validationMethod')?.toString();
            const method: CodeValidationMethod | null = posted === 'email' || posted === 'phone' ? posted : null;
            if (!step || !method) {
                return { success: false, error: t('extPriceshoes:login.errors.generic') };
            }

            const codeStep = {
                customerStatus: 'PENDING' as const,
                ...step,
                verificationMethod: method,
                identityFlow: formData.get(IDENTITY_FLOW_FIELD)?.toString(),
            };
            const errorFor = (key: string) => t(`extPriceshoes:login.verification.errors.${key}`);

            if (loginMode === 'priceshoesResendCode') {
                const sending = await sendVerificationCode(context, step.identifier, method);
                const identityFlow =
                    sending.result === 'sent' ? sealIdentityFlow(context, { customerId: sending.customerId }) : null;
                if (identityFlow) {
                    return { success: true, ...codeStep, identityFlow, codeResent: true };
                }
                const locked = sending.result === 'locked';
                return {
                    success: false,
                    error: errorFor(locked ? 'locked' : 'sendFailed'),
                    ...codeStep,
                    verificationLocked: locked,
                };
            }

            const code = formData.get('otp')?.toString().trim() ?? '';
            const codeError = validateVerificationCode(code);
            if (codeError) {
                return { success: false, error: errorFor(codeError), ...codeStep };
            }

            const flow = openIdentityFlow(formData);
            if (!flow) {
                // The validation expired (or was tampered with): choose the method again to get a new code.
                return {
                    success: false,
                    error: errorFor('expired'),
                    customerStatus: 'PENDING',
                    ...step,
                    selectedMethod: method,
                };
            }

            const verification = await verifyIdentity(context, flow.customerId, { otp: code });
            switch (verification.result) {
                case 'verified':
                    return (
                        identityVerifiedStep(context, step.identifier, {
                            customerId: flow.customerId,
                            changePasswordToken: verification.changePasswordToken,
                        }) ?? { success: false, error: errorFor('validateFailed'), ...codeStep }
                    );
                case 'invalid':
                    return { success: false, error: errorFor('invalidCode'), ...codeStep };
                case 'locked':
                    // Attempts for this code ran out: it can't be validated anymore, but a new one can be requested.
                    return { success: false, error: errorFor('codeLocked'), ...codeStep, verificationLocked: true };
                default:
                    return { success: false, error: errorFor('validateFailed'), ...codeStep };
            }
        }
        // @priceshoes-block-end LOGIN_IDENTITY_VERIFICATION

        // @priceshoes-block-start LOGIN_PASSWORD
        // Password screen of a VALID shopper (PRIC-171).
        if (loginMode === 'priceshoesPassword') {
            const identifier = formData.get('identifier')?.toString().trim() ?? '';
            // The identifier comes back from the password form; without a valid one, restart from identification.
            if (validateIdentifier(identifier)) {
                return { success: false, error: genericError };
            }

            const passwordError = validatePassword(formData.get('password')?.toString() ?? '');
            if (passwordError) {
                return {
                    success: false,
                    error: t(`extPriceshoes:login.password.errors.${passwordError}`),
                    step: 'password',
                    identifier,
                };
            }

            const login = await loginCustomer(context, identifier, formData.get('password')?.toString() ?? '');
            if (login.result !== 'authenticated') {
                const errorKey =
                    login.result === 'invalidCredentials'
                        ? 'login.password.errors.invalidCredentials'
                        : 'login.errors.generic';
                return { success: false, error: t(`extPriceshoes:${errorKey}`), step: 'password', identifier };
            }

            // Replace the guest session with the registered one and go Home (CA3), or back to returnUrl,
            // storing the Cognito tokens in encrypted httpOnly cookies (CA8).
            const wishlistMergeResult = await startCustomerSession(context, login.slasToken);
            const headers = new Headers();
            for (const cookie of await serializeCognitoCookies(context, login.cognitoToken)) {
                headers.append('Set-Cookie', cookie);
            }

            const returnUrl = getSafeReturnUrl(
                formData.get('returnUrl')?.toString()?.trim() || new URL(request.url).searchParams.get('returnUrl')
            );
            let target = buildUrlFromContext(returnUrl || routes.home, context);
            if (wishlistMergeResult) {
                const { url, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                target = url;
                headers.append('Set-Cookie', setCookie);
            }
            return redirect(target, { headers });
        }
        // @priceshoes-block-end LOGIN_PASSWORD

        if (loginMode === 'social') {
            // Social login flow
            if (!provider || !isSocialLoginEnabled) {
                return { success: false, error: genericError };
            }
            const socialCallback = config.features.socialLogin.callbackUri;
            const socialLoginRedirectURI = isAbsoluteURL(socialCallback)
                ? socialCallback
                : `${getAppOrigin(context)}${buildUrlFromContext(socialCallback, context)}`;
            const finalRedirectURI = redirectPath
                ? `${socialLoginRedirectURI}?redirectUrl=${redirectPath}`
                : socialLoginRedirectURI;
            const result = await authorizeIDP(context, {
                hint: provider,
                redirectURI: finalRedirectURI,
            });
            if (result.success && result.redirectUrl) {
                logger.info('Login: social redirect initiated', { provider });
                return redirect(result.redirectUrl);
            }
            logger.warn('Login: social authorization failed', { provider });
            return { success: false, error: genericError };
        } else if (loginMode === 'passwordless') {
            // Passwordless login flow
            if (!email) {
                return { success: false, error: genericError };
            }

            const turnstileToken = formData.get('turnstileToken')?.toString();
            const { allowed, cookieValue } = await enforceTurnstile({
                request,
                config,
                turnstileToken,
                logger,
                actionName: 'login-passwordless',
                email,
                turnstileCookieName: getCookieNameWithSiteId(COOKIE_TURNSTILE_VERIFIED, context),
            });
            if (!allowed) {
                return data(
                    { success: false, error: t('errors:api.forbidden'), errorCode: 'NOT_AUTHORIZED' },
                    { status: 403 }
                );
            }

            // Write the cc-tv cookie when enforceTurnstile performed a fresh siteverify.
            // Null means the request was already covered by a valid HMAC-bound cookie;
            // nothing new to record. The cookie lets downstream endpoints (e.g. OTP resend)
            // skip a fresh challenge within the 30-minute window.
            let loginTvHeaders: Record<string, string> | undefined;
            if (cookieValue !== null) {
                const tvCookie = createCookie<string>(
                    COOKIE_TURNSTILE_VERIFIED,
                    getCookieConfig({ httpOnly: true, maxAge: TURNSTILE_VERIFIED_MAX_AGE }, context),
                    context
                );
                loginTvHeaders = { 'Set-Cookie': await tvCookie.serialize(cookieValue) };
            }

            // Build redirectPath from returnUrl, action, and actionParams for passwordless flow
            const url = new URL(request.url);
            const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));
            const pendingAction = url.searchParams.get('action');
            const actionParams = url.searchParams.get('actionParams');

            // Construct redirectPath with all params encoded
            let finalRedirectPath = redirectPath || returnUrl || routes.home;
            if (returnUrl && (pendingAction || actionParams)) {
                const redirectParams = new URLSearchParams();
                if (pendingAction) {
                    redirectParams.set('action', pendingAction);
                }
                if (actionParams) {
                    redirectParams.set('actionParams', actionParams);
                }
                const queryString = redirectParams.toString();
                finalRedirectPath = queryString ? `${returnUrl}?${queryString}` : returnUrl;
            }

            try {
                await authorizePasswordless(context, { userid: email, redirectPath: finalRedirectPath });
                logger.info('Login: passwordless code sent');
                // Passwordless authorization sent - redirect to login page with OTP form
                const params = new URLSearchParams();
                params.set('otp', 'true');
                params.set('email', email);
                if (returnUrl) {
                    params.set('returnUrl', returnUrl);
                }
                if (pendingAction) {
                    params.set('action', pendingAction);
                }
                if (actionParams) {
                    params.set('actionParams', actionParams);
                }
                return loginTvHeaders
                    ? data({ success: true, showOTPForm: true, email }, { headers: loginTvHeaders })
                    : { success: true, showOTPForm: true, email };
            } catch (error) {
                const errorMessage = extractErrorMessage(error);
                const errorKey = getPasswordlessErrorMessageKey(errorMessage);

                logger.error('Login: passwordless authorize failed', {
                    status: error instanceof ApiError ? error.status : undefined,
                    errorMessage,
                    errorKey,
                    rawBody: error instanceof ApiError ? error.rawBody : undefined,
                });

                return loginTvHeaders
                    ? data({ success: false, error: t(errorKey) }, { headers: loginTvHeaders })
                    : { success: false, error: t(errorKey) };
            }
        } else {
            // Standard password login flow (default case - handles 'password' mode or undefined/null)
            if (!email || !password) {
                return { success: false, error: genericError };
            }
            // Snapshot the guest wishlist BEFORE the SLAS swap; the registered token can't authorize a read against the guest customerId.
            const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);
            const result = await loginRegisteredUser(context, { email, password }, { skipUsid });
            if (!result.success) {
                logger.warn('Login: standard login failed');
                return { success: false, error: genericError };
            }

            logger.info('Login: standard login succeeded');
            // Login successful - merge basket on server before redirecting
            let mergedBasket: Awaited<ReturnType<typeof mergeBasket>> | undefined;
            try {
                mergedBasket = await mergeBasket(context);
                if (mergedBasket) {
                    updateBasketResource(context, mergedBasket);
                }
            } catch (error) {
                logger.error('Login: basket merge failed', { error });
            }

            // Guest may have entered a different email at checkout contact-info before signing in.
            // transferBasket keeps the guest email; reconcile so the order shows the signed-in customer's email.
            // Skipped when no active basket exists (login is used outside checkout too).
            if (mergedBasket?.basketId) {
                try {
                    const auth = getAuth(context);
                    if (auth.customerId) {
                        const customer = await getCustomer(context, auth.customerId);
                        const customerEmail =
                            customer.email || (customer.login?.includes('@') ? customer.login : undefined);
                        const basketEmail = mergedBasket.customerInfo?.email;
                        if (customerEmail && basketEmail && basketEmail.toLowerCase() !== customerEmail.toLowerCase()) {
                            const clients = createApiClients(context);
                            const { data: reconciledBasket } = await clients.shopperBasketsV2.updateCustomerForBasket({
                                params: { path: { basketId: mergedBasket.basketId } },
                                body: { email: customerEmail },
                            });
                            updateBasketResource(context, reconciledBasket);
                        }
                    }
                } catch (reconcileError) {
                    logger.error('Login: basket email reconciliation failed', { error: reconcileError });
                }
            }

            let wishlistMergeResult: WishlistMergeResult | null = null;
            if (guestWishlistSnapshot) {
                try {
                    wishlistMergeResult = await mergeWishlist(context, guestWishlistSnapshot);
                } catch (wishlistError) {
                    logger.error('Login: wishlist merge failed', { error: wishlistError });
                }
            }

            // Helper to prepare redirect with wishlist merge cookie. Uses redirectDocument (full
            // page navigation) rather than redirect only when passkeys are enabled — a
            // client-side transition never unloads the document, so a native passkey picker
            // left open by the login page's conditional mediation (usePasskeyLogin, below)
            // would otherwise persist visibly over the next page. An actual document unload is
            // the only reliable way to dismiss it. When passkeys are disabled, conditional
            // mediation never starts, so there's no picker to dismiss and a plain redirect
            // avoids the unnecessary full-page reload.
            //
            // Background re-authentication (account page password/email change) submits here via
            // a fetcher and opts out with `skipDocumentRedirect`: it never opened a picker, and a
            // document reload would tear down the page — including the just-queued success toast —
            // before it can render.
            const skipDocumentRedirect = formData.get('skipDocumentRedirect') === 'true';
            const doRedirect = config.features.passkey.enabled && !skipDocumentRedirect ? redirectDocument : redirect;
            const prepareRedirect = (target: string) => {
                if (wishlistMergeResult) {
                    const { url, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                    return doRedirect(url, { headers: { 'Set-Cookie': setCookie } });
                }
                return doRedirect(target);
            };

            // Login successful - redirect to returnUrl if provided, otherwise home
            // Try to get returnUrl from formData first (in case it was submitted as hidden input)
            // Otherwise fall back to URL query params
            const returnUrlFromForm = formData.get('returnUrl')?.toString()?.trim();
            const returnUrlFromUrl = new URL(request.url).searchParams.get('returnUrl');
            const returnUrl = getSafeReturnUrl(returnUrlFromForm || returnUrlFromUrl);

            // Get action and actionParams to preserve in redirect URL
            const actionFromForm = formData.get('action')?.toString();
            const actionParamsFromForm = formData.get('actionParams')?.toString();
            const actionFromUrl = new URL(request.url).searchParams.get('action');
            const actionParamsFromUrl = new URL(request.url).searchParams.get('actionParams');
            const pendingAction = actionFromForm || actionFromUrl;
            const actionParams = actionParamsFromForm || actionParamsFromUrl;

            // Redirect to returnUrl (with preserved action params) or home.
            // `returnUrl` is guaranteed relative by `getSafeReturnUrl` above, so we mutate
            // its query string in place rather than constructing a `URL` object; that
            // keeps the redirect target relative end-to-end and removes any temptation
            // for a future refactor to forward an absolute URL past the open-redirect
            // guard.
            if (returnUrl) {
                if (pendingAction || actionParams) {
                    const [pathAndQuery, fragment] = returnUrl.split('#');
                    const [pathname, existingQuery] = pathAndQuery.split('?');
                    const params = new URLSearchParams(existingQuery);
                    if (pendingAction) params.set('action', pendingAction);
                    if (actionParams) params.set('actionParams', actionParams);
                    const queryString = params.toString();
                    const target = pathname + (queryString ? `?${queryString}` : '') + (fragment ? `#${fragment}` : '');
                    return prepareRedirect(buildUrlFromContext(target, context));
                }
                return prepareRedirect(buildUrlFromContext(returnUrl, context));
            }

            return prepareRedirect(buildUrlFromContext(routes.home, context));
        }
    } catch {
        return { success: false, error: genericError };
    }
}

export default function Login({ loaderData }: { loaderData: LoginLoaderData }): ReactElement {
    const { t } = useTranslation('login');
    const actionData = useActionData<typeof action>();
    const navigate = useNavigate();
    const config = useConfig();

    const { error: loaderError, passwordlessSent, email, returnUrl, showOTPForm, otpLength, pageUrl } = loaderData;

    const [resendTurnstileToken, setResendTurnstileToken] = useState<string | null>(null);
    const resendTurnstileResetRef = useRef<(() => void) | null>(null);
    const [resendTurnstileBypassed, setResendTurnstileBypassed] = useState(false);
    const [passkeyLoginError, setPasskeyLoginError] = useState<string | null>(null);

    const { loginWithPasskey, abortPasskeyLogin } = usePasskeyLogin(
        (result) => {
            const target = returnUrl || routes.home;
            if (result.wishlistMerge) {
                const separator = target.includes('?') ? '&' : '?';
                void navigate(`${target}${separator}wishlistMerge=${result.wishlistMerge}`);
            } else {
                void navigate(target);
            }
        },
        // The shopper picked a passkey but the server couldn't complete the login — surface it
        // instead of leaving the sign-in form silently unchanged.
        () => setPasskeyLoginError(t('passkeyError'))
    );

    // Conditional mediation on mount — the browser surfaces matching passkeys as autofill
    // suggestions on the email input (autoComplete="username webauthn" in StandardLoginForm).
    // Abort on unmount so a pending ceremony doesn't resolve after the shopper navigates away.
    useEffect(() => {
        if (!config?.features.passkey.enabled) return;
        void loginWithPasskey();
        return () => abortPasskeyLogin();
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
    }, [config?.features.passkey.enabled]);

    const turnstileEnabled = config ? isTurnstileEnabled(config) : false;
    const turnstileMode = config ? getTurnstileMode(config) : 'managed';
    const turnstileSiteKey = useMemo(() => {
        if (!config || !turnstileEnabled) return null;
        if (typeof window !== 'undefined') {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            return getTurnstileSiteKey(config, baseUrl);
        }
        return null;
    }, [config, turnstileEnabled]);

    const handleResendTurnstileSuccess = useCallback((token: string) => {
        setResendTurnstileToken(token);
    }, []);
    const handleResendTurnstileError = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);
    const handleResendTurnstileExpire = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);
    // CDN failure — resend proceeds without a token; the server may allow it based on
    // the existing cc-tv cookie set when the first OTP was dispatched.
    const handleResendTurnstileBypass = useCallback(() => {
        setResendTurnstileBypassed(true);
    }, []);
    // Prefer actionData error (from form submission) over loaderData error (from URL params);
    // a post-gesture passkey failure takes precedence since it reflects the shopper's latest action.
    const error = passkeyLoginError || actionData?.error || loaderError || undefined;

    // Check if we should show OTP form from actionData (after email submission) or loaderData (from URL)
    const shouldShowOTPForm = actionData?.showOTPForm || showOTPForm;
    const showOTPModal = Boolean(shouldShowOTPForm);
    const otpEmail = actionData?.email || email;

    // Show passwordless success state
    if (passwordlessSent && email) {
        return (
            <div
                data-section="auth"
                className="min-h-screen flex items-center justify-center bg-background py-12 section-container">
                <div className="max-w-md w-full space-y-8">
                    <Card className="p-8">
                        <div className="text-center space-y-4">
                            <h1 className="text-2xl font-semibold">{t('checkEmailTitle')}</h1>
                            <p className="text-sm text-muted-foreground">{t('checkEmailDescription', { email })}</p>
                            <Link
                                to={routes.login}
                                className="inline-flex items-center text-sm text-primary hover:text-primary/80">
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <>
            <SeoMeta
                title={t('meta.title', { defaultValue: 'Sign In' })}
                description={t('meta.description', {
                    defaultValue: 'Sign in to your account to view orders, manage your wishlist, and more.',
                })}
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div
                data-section="auth"
                className="min-h-screen flex items-center justify-center bg-background py-12 section-container">
                <div className="max-w-md w-full space-y-8">
                    {/* @priceshoes-block-start LOGIN_IDENTIFICATION */}
                    {/* PriceShoes sign-in starts by identifying the shopper (partner ID or email); it replaces the template's password, passwordless and social forms. */}
                    {/* @priceshoes-block-start LOGIN_PASSWORD */}
                    {actionData?.step === 'password' && actionData.identifier ? (
                        <PasswordForm identifier={actionData.identifier} error={error} returnUrl={returnUrl} />
                    ) : actionData?.identityVerified && actionData.identityFlow ? (
                        // @priceshoes-line LOGIN_IDENTITY_VERIFICATION - Identity validated; PRIC-169 creates the password here.
                        <IdentityVerified identityFlow={actionData.identityFlow} />
                    ) : actionData?.verificationMethod &&
                      actionData.validations?.length &&
                      actionData.identifier &&
                      actionData.identityFlow ? (
                        // @priceshoes-line LOGIN_IDENTITY_VERIFICATION - Verification code sent by email or SMS (PRIC-168).
                        <VerificationCodeForm
                            identifier={actionData.identifier}
                            validations={actionData.validations}
                            identityFlow={actionData.identityFlow}
                            method={actionData.verificationMethod}
                            error={error}
                            resent={actionData.codeResent}
                            locked={actionData.verificationLocked}
                            returnUrl={returnUrl}
                        />
                    ) : actionData?.securityQuestions?.length &&
                      actionData.validations?.length &&
                      actionData.identifier &&
                      actionData.identityFlow ? (
                        // @priceshoes-line LOGIN_IDENTITY_VERIFICATION - Security questions (PRIC-168).
                        <SecurityQuestionsForm
                            identifier={actionData.identifier}
                            validations={actionData.validations}
                            identityFlow={actionData.identityFlow}
                            questions={actionData.securityQuestions}
                            error={error}
                            returnUrl={returnUrl}
                        />
                    ) : actionData?.validations?.length && actionData.identifier ? (
                        // @priceshoes-line LOGIN_VALIDATION_METHOD - Identity validation method of a PENDING shopper (PRIC-167).
                        <ValidationMethodForm
                            identifier={actionData.identifier}
                            validations={actionData.validations}
                            selectedMethod={actionData.selectedMethod}
                            disabledMethods={actionData.disabledMethods}
                            partnerId={actionData.partnerId}
                            error={error}
                            returnUrl={returnUrl}
                        />
                    ) : (
                        <IdentificationForm error={error} returnUrl={returnUrl} />
                    )}
                    {/* @priceshoes-block-end LOGIN_PASSWORD */}
                    {/* @priceshoes-block-end LOGIN_IDENTIFICATION */}
                </div>
            </div>

            {/* Turnstile widget for OTP resend - hidden, generates tokens for the resend fetch */}
            {showOTPModal && turnstileEnabled && turnstileSiteKey && !resendTurnstileBypassed && (
                <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onSuccess={handleResendTurnstileSuccess}
                    onError={handleResendTurnstileError}
                    onExpire={handleResendTurnstileExpire}
                    onBypass={handleResendTurnstileBypass}
                    enabled={turnstileEnabled}
                    mode={turnstileMode}
                    resetRef={resendTurnstileResetRef}
                />
            )}

            {/* OTP Modal - appears over the login page */}
            <OtpModal
                isOpen={showOTPModal}
                otpLength={otpLength}
                initialError={loaderError}
                onClose={() => {
                    // Navigate back to login without OTP modal
                    const url = new URL(window.location.href);
                    url.searchParams.delete('otp');
                    url.searchParams.delete('error');
                    url.searchParams.delete('token');
                    url.searchParams.delete('email');
                    window.history.replaceState({}, '', url.toString());
                    window.location.reload();
                }}
                email={otpEmail || ''}
                onSuccess={(_tokenResponse, meta) => {
                    // Redirect to return URL or home after successful login.
                    // Forward the wishlist merge flag so the destination route can render a toast.
                    const target = returnUrl || routes.home;
                    if (meta?.wishlistMerge) {
                        const separator = target.includes('?') ? '&' : '?';
                        void navigate(`${target}${separator}wishlistMerge=${meta.wishlistMerge}`);
                    } else {
                        void navigate(target);
                    }
                }}
                onResendCode={async () => {
                    const formData = new FormData();
                    formData.append('email', otpEmail || '');
                    formData.append('loginMode', 'passwordless');
                    if (returnUrl) {
                        formData.append('redirectPath', returnUrl);
                    }
                    if (resendTurnstileToken) {
                        formData.append('turnstileToken', resendTurnstileToken);
                    }
                    await fetch(window.location.pathname, {
                        method: 'POST',
                        body: formData,
                    });
                    if (turnstileEnabled) {
                        setResendTurnstileToken(null);
                        resendTurnstileResetRef.current?.();
                    }
                }}
            />
        </>
    );
}
